/**
 * SifremiUnuttum — herkese açık "şifremi unuttum" sayfası (backlog #570, backend #569). Rota: /sifremi-unuttum.
 *
 * Tek alan: giriş adı ya da e-posta adresi. `POST /public/sifremi-unuttum` HER hesap için aynı cevabı verir; sayfa da bu cevabı olduğu gibi
 * (kendi TR / EN metniyle) gösterir ve bir hesabın var olup olmadığına dair hiçbir şey ima etmez. Cevap sonrası form kalkar, Giriş'e dönen bir
 * bağlantı çıkar (e-posta gelmezse spam klasörüne bakılması söylenir). Bir sınır aşılırsa (429) sayfa bunu söyler ve, backend `Retry-After`'ı
 * okunur kılmışsa, kaç dakika beklenmesi gerektiğini yazar. Hiçbir şey yazılmadıysa (400) form yerinde kalır.
 *
 * Şifre belirleme sayfası gibi (SifreBelirle) başlık çubuğu yok, kendi TR / EN anahtarı var; backend metinleri istekteki dilde gelir ama
 * hata ve sonuç `errorKey` / `mesajKey`'e göre sayfanın t() metninden çizilir (dil değişince hemen güncellenir). Yazılan ad adres çubuğuna
 * yazılmaz ve saklanmaz.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, CardBody, Col, Container, Form, Input, Label, Row, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { LanguageContext } from '../../../Components/Contexts/LanguageContext';
import LanguageDropdown from '../../../Components/Common/LanguageDropdown';
import logoLight from '../../../assets/images/logo-light.png';
import { STORAGE_KEY } from '../../../i18n';
import { sifremiUnuttumTalebi } from '../../../services/sifremiUnuttumService';

const SifremiUnuttum = () => {
  const { t } = useTranslation('giris');

  // Dil: hiçbir seçim yoksa ilk açılışta tarayıcının dili (Türkçe değilse İngilizce), sonra kullanıcının seçimi hatırlanır (SifreBelirle ile aynı).
  const { selectedLang, setSelectedLang } = useContext(LanguageContext);
  useEffect(() => {
    let stored = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* özel pencere */ }
    if (!stored) setSelectedLang(/^tr\b/i.test(navigator.language || '') ? 'tr' : 'en');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { document.title = t('sifremiUnuttum.sayfaBasligi'); }, [t]);

  const [kullanici, setKullanici] = useState('');
  const [gonderiyor, setGonderiyor] = useState(false);
  const [hata, setHata] = useState(null);     // { errorKey, mesaj, retryAfter }
  const [bitti, setBitti] = useState(null);   // 200 cevabı: hep aynı

  function hatayiYaz(h) {
    if (!h) return null;
    if (h.errorKey === 'sifre.unuttum_kullanici_gerekli') return t('sifremiUnuttum.kullaniciGerekli');
    if (h.errorKey === 'sifre.unuttum_cok_sik') {
      return h.retryAfter
        ? t('sifremiUnuttum.cokSikDakika', { dakika: Math.max(1, Math.ceil(h.retryAfter / 60)) })
        : t('sifremiUnuttum.cokSik');
    }
    return h.mesaj || t('sifremiUnuttum.baglantiHatasi');
  }

  async function gonder(e) {
    e.preventDefault();
    if (!kullanici.trim()) { setHata({ errorKey: 'sifre.unuttum_kullanici_gerekli' }); return; }
    setHata(null);
    setGonderiyor(true);
    try {
      setBitti(await sifremiUnuttumTalebi(kullanici));
      setKullanici('');
    } catch (err) {
      setHata({ errorKey: err.errorKey, mesaj: err.status ? err.message : null, retryAfter: err.retryAfter });
    } finally {
      setGonderiyor(false);
    }
  }

  const icerik = bitti ? (
    <div className="text-center mt-2">
      <h5 className="text-body">{t('sifremiUnuttum.talepAlindiBaslik')}</h5>
      <p className="text-muted">{t('sifremiUnuttum.talepAlindi')}</p>
      <Link to="/Login" className="btn btn-primary w-100 mt-2">{t('sifremiUnuttum.girisSayfasinaDon')}</Link>
    </div>
  ) : (
    <>
      <div className="text-center mt-2">
        <h5 className="text-body">{t('sifremiUnuttum.baslik')}</h5>
        <p className="text-muted mb-0">{t('sifremiUnuttum.aciklama')}</p>
      </div>
      {hata && <Alert color="danger" fade={false} className="mt-3 mb-0">{hatayiYaz(hata)}</Alert>}
      <div className="p-2">
        <Form onSubmit={gonder}>
          <div className="mb-3">
            <Label htmlFor="sifremi-unuttum-kullanici" className="form-label">{t('sifremiUnuttum.alan')}</Label>
            <Input
              id="sifremi-unuttum-kullanici" name="kullanici" type="text" className="form-control" autoComplete="username" autoFocus
              maxLength={255} value={kullanici} onChange={(e) => setKullanici(e.target.value)} invalid={Boolean(hata)}
            />
          </div>
          <Button color="primary" className="w-100" type="submit" disabled={gonderiyor || !kullanici.trim()}>
            {gonderiyor ? <Spinner size="sm" /> : t('sifremiUnuttum.gonder')}
          </Button>
          <div className="text-center mt-3">
            <Link to="/Login" className="text-muted">{t('sifremiUnuttum.girisSayfasinaDon')}</Link>
          </div>
        </Form>
      </div>
    </>
  );

  return (
    <div className="auth-page-wrapper pt-5">
      <div className="auth-one-bg-position auth-one-bg" id="auth-particles">
        <div className="bg-overlay"></div>
        <div className="shape">
          <svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 1440 120">
            <path d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"></path>
          </svg>
        </div>

        <div className="position-absolute top-0 end-0 p-3" style={{ zIndex: 10 }}>
          <LanguageDropdown selectedLang={selectedLang} setSelectedLang={setSelectedLang} />
        </div>

        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <img src={logoLight} alt="" height="100" />
                </div>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">{icerik}</CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default SifremiUnuttum;
